import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ONBOARDING_STAGE_REGISTRATION_SUBMITTED } from '../../util/onboardingStages';
import NamedLink from '../NamedLink/NamedLink';

import css from './OnboardingStepper.module.css';

const STAGE_COUNT = 5;

const STAGE_LABEL_IDS = [
  'OnboardingStepper.stage1Label',
  'OnboardingStepper.stage2Label',
  'OnboardingStepper.stage3Label',
  'OnboardingStepper.stage4Label',
  'OnboardingStepper.stage5Label',
];

const clampStage = stage => Math.min(Math.max(stage || 0, 0), STAGE_COUNT);

/**
 * The 5-node UNGM-style onboarding progress indicator. One component, two
 * variants — the `full` stepper (the /onboarding hub, EmailVerificationPage)
 * and the `compact` one-line strip (Topbar). See docs/onboarding-spec.md §5.
 *
 * Presentational only: the stage number and approval flag are computed by
 * util/onboardingStages.js and handed in as props.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.rootClassName
 * @param {string?} props.className
 * @param {number} props.stage 0-5, from getOnboardingStage()
 * @param {boolean} [props.isApproved] once true, all 5 nodes render complete
 * @param {'full'|'compact'} [props.variant]
 * @param {boolean} [props.linkToHub] compact only — render the strip as a link
 *   to the onboarding hub. The Topbar sets this: the strip is the one piece of
 *   onboarding UI on every page, so it has to be the way back into
 *   registration, not just a read-out of how stuck the member is.
 */
const OnboardingStepper = props => {
  const {
    rootClassName,
    className,
    stage,
    isApproved = false,
    variant = 'full',
    linkToHub = false,
  } = props;
  const intl = useIntl();

  // Terminal state: the panel below says "approved", the stepper itself just
  // shows every node finished rather than adding a 6th node for it.
  const effectiveStage = isApproved ? STAGE_COUNT : clampStage(stage);

  const progressLabel = intl.formatMessage(
    { id: 'OnboardingStepper.stepOfSteps' },
    { current: effectiveStage, total: STAGE_COUNT }
  );

  const screenreaderLabel = intl.formatMessage(
    { id: 'OnboardingStepper.screenreader.progress' },
    { current: effectiveStage, total: STAGE_COUNT }
  );

  const currentStageLabel = intl.formatMessage({
    id: STAGE_LABEL_IDS[Math.max(effectiveStage - 1, 0)],
  });

  const classes = classNames(rootClassName || css.root, className);

  if (variant === 'compact') {
    // The CTA label has to change at stage 5: there is nothing left to
    // "continue" once the registration is submitted, only a review to wait on.
    const ctaId =
      effectiveStage >= ONBOARDING_STAGE_REGISTRATION_SUBMITTED
        ? 'OnboardingStepper.compactCtaSubmitted'
        : 'OnboardingStepper.compactCta';

    const compactContent = (
      <>
        <span className={css.compactLabel}>
          {progressLabel} · {currentStageLabel}
        </span>
        <span className={css.compactTrack}>
          <span
            className={css.compactFill}
            style={{ width: `${(effectiveStage / STAGE_COUNT) * 100}%` }}
          />
        </span>
        {linkToHub ? (
          <span className={css.compactCta}>
            <FormattedMessage id={ctaId} />
            <span aria-hidden="true"> →</span>
          </span>
        ) : null}
      </>
    );

    return linkToHub ? (
      <NamedLink
        name="OnboardingPage"
        className={classNames(classes, css.compact, css.compactLink)}
        aria-label={screenreaderLabel}
      >
        {compactContent}
      </NamedLink>
    ) : (
      <div className={classNames(classes, css.compact)} role="status" aria-label={screenreaderLabel}>
        {compactContent}
      </div>
    );
  }

  return (
    <div className={classes}>
      <ol className={css.nodeList} aria-label={screenreaderLabel}>
        {STAGE_LABEL_IDS.map((labelId, index) => {
          const nodeNumber = index + 1;
          const isComplete = effectiveStage >= nodeNumber;
          const isCurrent = !isComplete && effectiveStage + 1 === nodeNumber;
          return (
            <li
              key={labelId}
              className={classNames(css.node, {
                [css.nodeComplete]: isComplete,
                [css.nodeCurrent]: isCurrent,
              })}
            >
              <span className={css.nodeMarker} aria-hidden="true">
                {isComplete ? '✓' : nodeNumber}
              </span>
              <span className={css.nodeLabel}>
                <FormattedMessage id={labelId} />
              </span>
            </li>
          );
        })}
      </ol>

      {/* < --viewportMedium: a five-node row does not fit, collapse to one line. */}
      <div className={css.mobileFallback} role="status" aria-hidden="true">
        <span className={css.compactLabel}>
          {progressLabel} · {currentStageLabel}
        </span>
        <span className={css.compactTrack}>
          <span
            className={css.compactFill}
            style={{ width: `${(effectiveStage / STAGE_COUNT) * 100}%` }}
          />
        </span>
      </div>
    </div>
  );
};

export default OnboardingStepper;
